import { getApiClient } from '../axios-config';

/**
 * Timecard Approvals API Service
 * Handles all API calls related to timecard approvals
 */
class TimecardApprovalsAPI {
  /**
   * Get approval status for a specific foreman and week
   * @param {string} foremanId - Foreman ID
   * @param {string} weekEndingDate - Week ending date (YYYY-MM-DD format)
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Approval status object
   */
  static async getApprovalStatus(foremanId, weekEndingDate, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      `/api/timecard-approvals/${foremanId}/${weekEndingDate}`
    );
    return response.data.data.status || null;
  }

  /**
   * Get all approvals for a week
   * @param {string} weekEndingDate - Week ending date (YYYY-MM-DD format)
   * @param {string} token - JWT authentication token
   * @returns {Promise<Array>} Array of approval status objects
   */
  static async getWeekApprovals(weekEndingDate, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      `/api/timecard-approvals/week/${weekEndingDate}`
    );
    return response.data.data.statuses || [];
  }

  /**
   * Approve a week for a foreman
   * @param {string} foremanId - Foreman ID
   * @param {string} weekEndingDate - Week ending date (YYYY-MM-DD format)
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Approval result
   */
  static async approveWeek(foremanId, weekEndingDate, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post('/api/timecard-approvals/', {
      foreman_id: foremanId,
      week_ending_date: weekEndingDate,
      notes: null, // Optional notes field
    });
    return response.data.data;
  }

  /**
   * Unapprove a week for a foreman
   * @param {string} foremanId - Foreman ID
   * @param {string} weekEndingDate - Week ending date (YYYY-MM-DD format)
   * @param {string} token - JWT authentication token
   * @returns {Promise<void>}
   */
  static async unapproveWeek(foremanId, weekEndingDate, token) {
    const apiClient = getApiClient(token);
    await apiClient.delete(
      `/api/timecard-approvals/${foremanId}/${weekEndingDate}`
    );
  }

  /**
   * Admin approves on behalf of a foreman
   * @param {string} foremanId - Foreman ID
   * @param {string} weekEndingDate - Week ending date (YYYY-MM-DD format)
   * @param {string} notes - Approval notes
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Approval result
   */
  static async adminApprove(foremanId, weekEndingDate, notes, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post(
      '/api/timecard-approvals/admin-approve',
      {
        foreman_id: foremanId,
        week_ending_date: weekEndingDate,
        notes,
      }
    );
    return response.data.data;
  }
}

export default TimecardApprovalsAPI;
